import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /**
   * REMOVED: this component used to push a history entry when the
   * drawer opened, specifically so that swipe-back / the browser back
   * button would close the drawer instead of navigating away — and
   * then listened for `popstate` to actually perform that close.
   *
   * That mechanism turned out to be the source of a long, hard-to-pin-
   * down scroll-freeze bug on mobile: opening the drawer applies a
   * scroll lock (collapsing document.documentElement to height:0px
   * among other things), and while a normal close (tap the close
   * button) reliably reverses that lock, a swipe-back-triggered close
   * went through a different code path that didn't consistently
   * restore everything — sometimes needing an extra swipe or an extra
   * touch to "unstick," and sometimes not recovering at all.
   *
   * Multiple targeted fixes (racing the close against a timeout,
   * forcing a reflow, explicitly restoring document.documentElement's
   * height, etc.) each fixed one failure mode but exposed another,
   * because the root issue is that swipe-back and a normal close were
   * two genuinely different code paths that needed to behave
   * identically, and kept drifting out of sync as the underlying
   * DialogComponent's own closing/animation logic changed timing.
   *
   * The reliable fix: don't intercept swipe-back / browser-back at
   * all. The cart drawer now closes ONLY through explicit user action
   * — the close button, clicking the backdrop, or Escape — all of
   * which go through the exact same close() → closeDialog() path,
   * which has always worked correctly. Swiping back or using the
   * browser's back button now behaves like normal browser navigation
   * (going to the previous page) instead of being intercepted to close
   * the drawer.
   */

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}